import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useSelector} from 'react-redux';

import {
  Card,
  CardHeader,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {
  Table,
  TableTopBar,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {SubTitleSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';

import {
  selectApplicantInformation,
  selectJobScoreCards,
} from 'src/selectors/crm';

import css from './scorecard.css';

// @ts-expect-error - TS7006 - Parameter 'arr' implicitly has an 'any' type.
const flatten = (arr) =>
  // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments.
  arr.reduce<Array<any>>(
    // @ts-expect-error - TS7006 - Parameter 'flat' implicitly has an 'any' type. | TS7006 - Parameter 'toFlatten' implicitly has an 'any' type.
    (flat, toFlatten) =>
      flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten),
    [],
  );

// @ts-expect-error - TS7006 - Parameter 'score' implicitly has an 'any' type.
const calculateAverage = (score) => {
  if (!score || score.count <= 0) {
    return '-';
  }
  return Math.round((score.total * 10) / score.count) / 10;
};

const ApplicantScorecards = ({jobId}: {jobId: number}) => {
  const {t} = useLabelTranslation();
  const applicantDetails = useSelector(selectApplicantInformation);
  const jobScoreCardData = useSelector(selectJobScoreCards);

  const {sections = [], ratingsOutOf} = jobScoreCardData ?? {};

  // Get current job application
  const currentJobApplication = applicantDetails?.applications?.find(
    (application: any) =>
      application.jobId === jobId &&
      !application.duplicate &&
      application.jobId > 0 &&
      application.active,
  );

  // Get all reviews grouped by step
  const {stepCandidates = []} = currentJobApplication ?? {};
  const allReviewsList = stepCandidates.map(
    // @ts-expect-error - TS2339 - Property 'interviewReviews' does not exist on type 'ApplicationStepCandidate'.
    (stepCandidate) => stepCandidate.interviewReviews,
  );
  // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments.
  const steps = flatten(allReviewsList).reduce<Record<string, any>>(
    // @ts-expect-error - TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'review' implicitly has an 'any' type.
    (acc, review) => {
      if (!acc[review.stepId]) {
        acc[review.stepId] = [];
      }
      acc[review.stepId].push(review);
      return acc;
    },
    {},
  );

  // Calculate scores
  const feedbackScores = flatten(Object.values(steps)).flatMap(
    // @ts-expect-error - TS7006 - Parameter 'review' implicitly has an 'any' type.
    (review) => review.feedbackScores,
  );

  const scoreCountMapByParameter: Record<string, any> = {};
  const finalScore = {total: 0, count: 0} as const;

  // @ts-expect-error - TS7031 - Binding element 'parameterId' implicitly has an 'any' type. | TS7031 - Binding element 'score' implicitly has an 'any' type.
  feedbackScores.forEach(({parameterId, score}) => {
    if (score < 0) {
      return;
    }

    // Update final score
    // @ts-expect-error - TS2540 - Cannot assign to 'total' because it is a read-only property.
    finalScore.total += score;
    if (score > 0) {
      // @ts-expect-error - TS2540 - Cannot assign to 'count' because it is a read-only property.
      finalScore.count += 1;
    }

    // Update parameter score
    if (!scoreCountMapByParameter[parameterId]) {
      scoreCountMapByParameter[parameterId] = {total: 0, count: 0};
    }
    scoreCountMapByParameter[parameterId].total += score;
    if (score > 0) {
      scoreCountMapByParameter[parameterId].count += 1;
    }
  });

  const overallScore = calculateAverage(finalScore);

  const renderScorecardSection = (section: any) => {
    // @ts-expect-error - TS7006 - Parameter 'parameter' implicitly has an 'any' type.
    const sectionRows = section.parameters.map((parameter) => {
      const averageScore = calculateAverage(
        scoreCountMapByParameter[parameter.id] || {total: 0, count: 0},
      );

      // Get individual reviewer scores
      const scores = Object.keys(steps).reduce<Record<string, any>>(
        (acc, stepId) => {
          // @ts-expect-error - TS7006 - Parameter 'review' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
          steps[stepId].forEach((review, index) => {
            const feedbackScore = review.feedbackScores.find(
              // @ts-expect-error - TS7006 - Parameter 'score' implicitly has an 'any' type.
              (score) => score.parameterId === parameter.id,
            );
            acc[`reviewer_${stepId}_${index}`] = feedbackScore?.score || '-';
          });
          return acc;
        },
        {},
      );

      return {
        id: `parameter_${parameter.id}`,
        parameter: parameter.name,
        averageScore,
        ...scores,
      };
    });

    const headers = [
      {key: 'parameter', label: t('Parameters', 'Parameters')},
      {key: 'averageScore', label: t('Average_Score', 'Average Score')},
      ...Object.keys(steps).flatMap((stepId) =>
        // @ts-expect-error - TS7006 - Parameter 'review' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
        steps[stepId].map((review, index) => ({
          key: `reviewer_${stepId}_${index}`,
          label: review.reviewer.fullName,
        })),
      ),
    ];

    return (
      <div key={section.name} className={css.sectionContainer}>
        <TableTopBar>
          <SubTitleSmall>{section.name}</SubTitleSmall>
        </TableTopBar>
        <Table
          borderRadius="0px 0px 12px 12px"
          headers={headers}
          entries={sectionRows}
          emptyText={t('No_data_available', 'No data available')}
        />
      </div>
    );
  };

  return (
    <div className={css.scorecardContainer}>
      <Card classNames={{wrapper: css.scorecardCardWrapper}}>
        <CardHeader className={css.scorecardHeaderContainer}>
          <SubTitleSmall>
            {t('Overall_Score', 'Overall Score')}:{' '}
            {typeof overallScore === 'number' && !Number.isInteger(overallScore)
              ? overallScore.toFixed(2)
              : overallScore}{' '}
            / {ratingsOutOf}
          </SubTitleSmall>
          <Tooltip
            title={`${t(
              'Reviewer_scores_are_based_on_a',
              'Reviewer scores are based on a',
            )} ${ratingsOutOf}-${t(
              'point_scale_adjustable_by_the_Admin_in_the_organization\'s_Settings',
              'point scale, adjustable by the Admin in the organization\'s Settings',
            )}.`}
            elevation="modal"
            placement="right"
            titleMaxLines={5}
          >
            <Icon name="info-circle" />
          </Tooltip>
        </CardHeader>
        <CardContent className={css.sectionsCardContent}>
          {sections.map(renderScorecardSection)}
        </CardContent>
      </Card>
    </div>
  );
};

export default ApplicantScorecards;
